import React, { useState } from 'react';
import api from '../services/api';
import { useDialog } from '../contexts/DialogContext';
import { motivosBloqueo as motivos } from './CatalogoBloqueosPanel';
import './CatalogoImportacionPreview.css';

const estados = {
  LISTA: 'Cambios de costo', REVISION_DESCRIPTIVA: 'Revisar datos', SIN_CAMBIOS: 'Sin cambios', BLOQUEADA: 'Bloqueada'
};
const campos = { costo: 'Costo', modelo: 'Modelo', medidas: 'Medida', madera: 'Madera', lustre: 'Lustre', tapizado: 'Tapizado' };
const formato = (valor, campo) => campo === 'costo'
  ? Number(valor).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 2 })
  : String(valor ?? '') || '-';

export const agruparDetalles = (destinos) => {
  const grupos = new Map();
  for (const destino of destinos) {
    const clave = JSON.stringify({ tipo: destino.tipo, modelo: destino.modelo, medidas: destino.medidas,
      lustre: destino.lustre, tapizado: destino.tapizado, cantidadTapizado: destino.cantidadTapizado,
      costoActual: destino.costoActual, costoNuevo: destino.costoNuevo,
      diferencia: destino.diferencia, porcentaje: destino.porcentaje, cambios: destino.cambios,
      variantesAfectadasPorNombre: destino.variantesAfectadasPorNombre });
    if (!grupos.has(clave)) grupos.set(clave, []);
    grupos.get(clave).push(destino);
  }
  return [...grupos.values()].flatMap(grupo => {
    const maderas = grupo.map(destino => String(destino.madera || '').trim().toUpperCase());
    if (grupo.length === 2 && maderas.includes('GUATAMBU') && maderas.includes('PARAISO')) {
      return [{ ...grupo[0], madera: 'GUATAMBU/PARAISO', claveVisual: grupo.map(destino => destino.varianteId).join('/') }];
    }
    return grupo.map(destino => ({ ...destino, claveVisual: destino.varianteId }));
  });
};

const CatalogoImportacionPreview = ({ onActualizado, categoria = 'RESPALDO' }) => {
  const recurso = ({ SILLA: 'sillas', RESPALDO: 'respaldos', SOFA: 'sofas', MUEBLES: 'muebles' })[categoria];
  const etiqueta = ({ SILLA: 'Sillas', RESPALDO: 'Respaldos', SOFA: 'Sofás', MUEBLES: 'Muebles' })[categoria];
  const camposMostrar = categoria === 'SOFA' ? { ...campos, lustre: 'Tapizado' } : campos;
  const { showConfirm } = useDialog();
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [archivo, setArchivo] = useState('');
  const [preview, setPreview] = useState(null);
  const [filtro, setFiltro] = useState('TODAS');
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(1);
  const [detalle, setDetalle] = useState(null);
  const [aplicando, setAplicando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [confirmarDescriptivos, setConfirmarDescriptivos] = useState(false);
  const soloRevision = preview?.permiteAplicar === false;

  const cargarArchivo = async (event) => {
    const seleccionado = event.target.files?.[0];
    event.target.value = '';
    if (!seleccionado) return;
    setPreview(null);
    setResultado(null);
    setConfirmarDescriptivos(false);
    setDetalle(null);
    setError('');
    setArchivo(seleccionado.name);
    if (!seleccionado.name.toLowerCase().endsWith('.csv') || seleccionado.size > 512 * 1024) {
      setError('Seleccioná un archivo CSV de hasta 512 KB.');
      return;
    }
    setCargando(true);
    try {
      const contenido = await seleccionado.arrayBuffer();
      let texto;
      try { texto = new TextDecoder('utf-8', { fatal: true }).decode(contenido); }
      catch { texto = new TextDecoder('windows-1252').decode(contenido); }
      const resultado = await api.post(`/catalogo/importacion/${recurso}/preview`, texto,
        { headers: { 'Content-Type': 'text/csv' } });
      setPreview(resultado);
      setFiltro('TODAS');
      setBusqueda('');
      setPagina(1);
    } catch (problema) {
      setError(problema.message || String(problema));
    } finally { setCargando(false); }
  };

  const aplicarCambios = async () => {
    const cantidadAplicable = categoria === 'MUEBLES'
      ? preview?.resumen.listas + preview?.resumen.sinCambios
      : preview?.resumen.listas + preview?.resumen.revisionDescriptiva;
    if (soloRevision || !preview?.previewId || aplicando || resultado || !cantidadAplicable) return;
    const detalleCambios = categoria === 'MUEBLES'
      ? `Se guardarán los vínculos de ${cantidadAplicable} filas identificadas y se actualizarán los costos de ${preview.resumen.listas}`
      : `Se actualizarán ${cantidadAplicable} filas`;
    const mensaje = categoria === 'MUEBLES'
      ? `${detalleCambios}. Las ${preview.resumen.bloqueadas} filas bloqueadas no se modificarán.`
      : `${detalleCambios}. Las ${preview.resumen.bloqueadas} filas bloqueadas no se modificarán.`;
    const confirmado = await showConfirm(mensaje,
      { title: `Aplicar cambios de ${etiqueta}`, confirmLabel: 'Aplicar cambios' }
    );
    if (!confirmado) return;
    setAplicando(true);
    setError('');
    try {
      const respuesta = await api.post(`/catalogo/importacion/${recurso}/aplicar`, {
        previewId: preview.previewId, confirmarDescriptivos, firmaResolucion: preview.firmaResolucion
      });
      setResultado(respuesta);
      if (onActualizado) await onActualizado();
    } catch (problema) {
      setError(problema.message || String(problema));
    } finally { setAplicando(false); }
  };

  const descargarBloqueadas = () => {
    const reporte = resultado?.reporteBloqueadas || preview?.reporteBloqueadas;
    if (!reporte) return;
    const blob = new Blob([reporte], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = `${recurso}-filas-bloqueadas.csv`;
    document.body.appendChild(enlace);
    enlace.click();
    enlace.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const filas = (preview?.filas || []).filter(fila => (filtro === 'TODAS' || fila.estado === filtro)
    && `${fila.idCliente} ${fila.descripcion}`.toLowerCase().includes(busqueda.toLowerCase().trim()));
  const paginas = Math.max(1, Math.ceil(filas.length / 20));
  const paginaVisible = Math.min(pagina, paginas);

  return (
    <>
      <button className="btn-actualizacion-global" onClick={() => setAbierto(true)}>Cargar CSV de {etiqueta}</button>
      {abierto && (
        <div className="modal-overlay importacion-overlay" onClick={() => !cargando && !aplicando && setAbierto(false)}>
          <section className="modal-content importacion-preview" role="dialog" aria-modal="true"
            aria-labelledby={`importacion-titulo-${recurso}`} onClick={event => event.stopPropagation()}>
            <div className="modal-header">
              <h2 id={`importacion-titulo-${recurso}`}>Revisión de {etiqueta}</h2>
              <button className="modal-close" aria-label="Cerrar vista previa" disabled={cargando || aplicando}
                onClick={() => setAbierto(false)}>×</button>
            </div>
            <div className="importacion-body">
              <div className="importacion-archivo">
                <label htmlFor={`importacion-csv-${recurso}`}>Archivo CSV</label>
                <input id={`importacion-csv-${recurso}`} type="file" accept=".csv,text/csv" disabled={cargando || aplicando} onChange={cargarArchivo} />
                <span className="importacion-modo">{resultado ? 'Actualización finalizada' : soloRevision ? 'Solo revisión' : 'Vista previa'}</span>
              </div>
              {archivo && <p className="importacion-nombre">{archivo}</p>}
              {error && <p className="importacion-error" role="alert">{error}</p>}
              {cargando && <p role="status">Analizando archivo...</p>}
              {preview && !resultado && preview.resumen.bloqueadas > 0 && preview.reporteBloqueadas && (
                <button className="btn-actualizacion-global" onClick={descargarBloqueadas}>Descargar filas bloqueadas (CSV)</button>
              )}
              {resultado && (
                <section className="importacion-resultado" aria-live="polite">
                  <h3>Actualización finalizada</h3>
                  <div className="importacion-resumen">
                    <span>Filas detectadas: <strong>{resultado.resumen.detectadas}</strong></span>
                    <span>Actualizadas: <strong>{resultado.resumen.actualizadas}</strong></span>
                    <span>Sin cambios: <strong>{resultado.resumen.sinCambios}</strong></span>
                    <span>Bloqueadas: <strong>{resultado.resumen.bloqueadas}</strong></span>
                  </div>
                  {resultado.resumen.bloqueadas > 0 && <button className="btn-actualizacion-global"
                    onClick={descargarBloqueadas}>Descargar filas bloqueadas (CSV)</button>}
                </section>
              )}
              {preview && (
                <>
                  <div className="importacion-resumen" aria-live="polite">
                    <span>Total: <strong>{preview.resumen.total}</strong></span>
                    <span>Costos: <strong>{preview.resumen.listas}</strong></span>
                    <span>Revisar datos: <strong>{preview.resumen.revisionDescriptiva}</strong></span>
                    <span>Sin cambios: <strong>{preview.resumen.sinCambios}</strong></span>
                    <button type="button" className="importacion-ir-bloqueadas" disabled={aplicando}
                      onClick={() => { setFiltro('BLOQUEADA'); setPagina(1); setDetalle(null); }}>Bloqueadas: <strong>{preview.resumen.bloqueadas}</strong> / {preview.casosBloqueados?.length || 0} casos</button>
                  </div>
                  <div className="importacion-filtros">
                    <label>Estado
                      <select value={filtro} disabled={aplicando} onChange={event => { setFiltro(event.target.value); setPagina(1); setDetalle(null); }}>
                        <option value="TODAS">Todos</option>
                        {Object.entries(estados).map(([valor, etiqueta]) => <option key={valor} value={valor}>{etiqueta}</option>)}
                      </select>
                    </label>
                    <label>Producto o ID
                      <input type="search" value={busqueda} disabled={aplicando}
                        onChange={event => { setBusqueda(event.target.value); setPagina(1); setDetalle(null); }} />
                    </label>
                  </div>
                  <div className="importacion-tabla-scroll">
                    <table className="importacion-tabla">
                      <thead><tr><th>ID cliente / Producto</th><th>Estado</th><th>Campos propuestos</th><th>Detalle</th></tr></thead>
                      <tbody>
                        {filas.slice((paginaVisible - 1) * 20, paginaVisible * 20).map(fila => (
                          <React.Fragment key={fila.renglon}>
                            <tr>
                              <td><strong>{fila.idCliente || '-'}</strong><br />{fila.descripcion || '-'}<br />
                                <small>Fila {fila.renglon} · {fila.madera} · {fila.medidas} · {fila.lustre} · {fila.tapizado}</small></td>
                              <td><span className={`importacion-estado ${fila.estado.toLowerCase()}`}>{estados[fila.estado]}</span></td>
                              <td>{[...new Set(fila.destinos.flatMap(destino => destino.cambios.map(cambio => camposMostrar[cambio.campo])))].join(', ') || '-'}</td>
                              <td><button className="btn-icon" aria-expanded={detalle === fila.renglon}
                                aria-label={detalle === fila.renglon ? 'Ocultar detalle' : 'Ver cambios y motivos'}
                                title={detalle === fila.renglon ? 'Ocultar detalle' : 'Ver cambios y motivos'}
                                onClick={() => setDetalle(detalle === fila.renglon ? null : fila.renglon)}>{detalle === fila.renglon ? '−' : '+'}</button></td>
                            </tr>
                            {detalle === fila.renglon && (
                              <tr><td colSpan="4" className="importacion-detalle">
                                {fila.motivos.map(motivo => <p className="importacion-error" key={motivo}>{motivos[motivo] || motivo}</p>)}
                                {fila.avisos.map(aviso => <p className="importacion-aviso" key={aviso}>{aviso}</p>)}
                                {agruparDetalles(fila.destinos).map(destino => (
                                  <div className="importacion-destino" key={destino.claveVisual}>
                                    <h3>{destino.tipo} · {destino.modelo} · {destino.medidas} · {destino.madera} · {destino.lustre} · {destino.tapizado}</h3>
                                    {destino.variantesAfectadasPorNombre > 0 && <p className="importacion-aviso">El nombre afecta a las {destino.variantesAfectadasPorNombre} variantes de este producto.</p>}
                                    {destino.cambios.length ? (
                                      <table><thead><tr><th>Campo</th><th>Actual</th><th>Propuesto</th><th>Alcance</th></tr></thead>
                                        <tbody>{destino.cambios.map(cambio => <tr key={cambio.campo}>
                                          <td>{camposMostrar[cambio.campo]}</td><td>{formato(cambio.actual, cambio.campo)}</td>
                                          <td>{formato(cambio.propuesto, cambio.campo)}</td><td>{cambio.alcance}</td>
                                        </tr>)}</tbody>
                                      </table>
                                    ) : <p>Sin cambios.</p>}
                                    {destino.diferencia !== null && <p>Diferencia de costo: {formato(destino.diferencia, 'costo')}
                                      {destino.porcentaje !== null ? ` (${destino.porcentaje}%)` : ''}</p>}
                                  </div>
                                ))}
                              </td></tr>
                            )}
                          </React.Fragment>
                        ))}
                        {!filas.length && <tr><td colSpan="4">No hay filas para este filtro.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  <div className="importacion-paginacion">
                    <button className="btn-icon" title="Página anterior" aria-label="Página anterior" disabled={paginaVisible === 1}
                      onClick={() => { setPagina(paginaVisible - 1); setDetalle(null); }}>‹</button>
                    <span>Página {paginaVisible} de {paginas} · {filas.length} filas</span>
                    <button className="btn-icon" title="Página siguiente" aria-label="Página siguiente" disabled={paginaVisible === paginas}
                      onClick={() => { setPagina(paginaVisible + 1); setDetalle(null); }}>›</button>
                  </div>
                </>
              )}
            </div>
            {preview && !resultado && !soloRevision && (
              <div className="importacion-aplicar">
                {preview.resumen.revisionDescriptiva > 0 && (
                  <label className="importacion-confirmacion">
                    <input type="checkbox" checked={confirmarDescriptivos} disabled={aplicando}
                      onChange={event => setConfirmarDescriptivos(event.target.checked)} />
                    Confirmo los cambios de nombre, medidas y otros datos descriptivos.
                  </label>
                )}
                <button className="btn-actualizacion-global" onClick={aplicarCambios}
                  disabled={aplicando || !preview.previewId
                    || !(categoria === 'MUEBLES' ? preview.resumen.listas + preview.resumen.sinCambios : preview.resumen.listas + preview.resumen.revisionDescriptiva)
                    || (preview.resumen.revisionDescriptiva > 0 && !confirmarDescriptivos)}>
                  {aplicando ? 'Aplicando cambios...' : 'Aplicar cambios'}
                </button>
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
};

export default CatalogoImportacionPreview;